/**
 * Formato de las notas: cómo se guarda lo que se escribe en el editor y cómo se
 * vuelve a pintar.
 *
 * HAY DOS FORMATOS DE ALMACENAMIENTO Y LOS DOS SIGUEN VIVOS:
 *
 *   1. TEXTO PLANO CON MARCAS — `**negrita**` y `==resaltado==`. Es el de
 *      siempre. Toda nota que solo use negrita y resaltado se sigue guardando
 *      así, sin tocar nada: se exporta a CSV, se manda por correo y se busca
 *      igual que antes.
 *
 *   2. HTML ACOTADO, precedido por el centinela `<!--dkt-rich-->`. Es el que se
 *      usa en cuanto la nota lleva algo que las marcas no saben expresar
 *      —cursiva, subrayado, alineación, tamaño, tipo de letra, color, listas—,
 *      porque el editor de notas ahora tiene la misma barra que el de correos
 *      (Lissette 2026-08-14: "quiero el mismo editor que tenemos para los mail
 *      en las notas del cliente").
 *
 * El centinela es un comentario HTML, así que una nota antigua jamás empieza por
 * él y la detección no se equivoca nunca. Y como el ascenso a HTML solo ocurre
 * cuando de verdad hace falta, una nota escrita a secas sigue siendo una cadena
 * de texto legible en cualquier volcado de la base de datos.
 *
 * SEGURIDAD. Las notas llevan datos del cliente y se pintan dentro de la app, así
 * que el HTML NUNCA se acepta tal cual: entra y sale por `parseNoteTree`, que es
 * una LISTA BLANCA. Solo sobreviven un puñado de etiquetas y un puñado de
 * propiedades de estilo, cada una con su valor validado; cualquier otra cosa
 * —un <script>, un onclick, un javascript: en un href, un <iframe>— se queda en
 * su texto y desaparece. Se limpia al guardar Y al leer: lo primero evita
 * almacenar basura, lo segundo protege también a las notas que ya estuvieran
 * guardadas o que llegaran por la API sin pasar por este editor.
 */

/** Escape text destined for innerHTML. */
export const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const MARK_STYLE = 'background:rgba(255,183,0,.32);color:inherit;border-radius:3px;padding:0 2px';

/** Escape para un valor de atributo: ademas de `&<>`, las comillas. Sin esto un
 *  font-family como "Times New Roman" cerraba el atributo style a media frase. */
const escapeAttr = (s) => escapeHtml(s).replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** Marca de las notas guardadas como HTML acotado. */
export const RICH_NOTE_PREFIX = '<!--dkt-rich-->';

/** ¿Esta nota está guardada como HTML acotado? */
export const isRichNote = (s) => String(s ?? '').startsWith(RICH_NOTE_PREFIX);

// ── Lista blanca ────────────────────────────────────────────────────────────

/**
 * Etiquetas que sobreviven. Cualquier otra aporta solo su contenido.
 *
 * NO hay listas a proposito. La barra del editor de correos —la que se pidio
 * copiar— no tiene boton de lista, y un <ul> pegado desde Word obligaria a
 * anidar bloques dentro del <span> con el que se pinta una nota, que es HTML
 * invalido. Un <li> pegado se aplana a una linea suelta: se ve igual y no
 * rompe el marcado de quien la muestre.
 */
const TAGS = new Set(['b', 'i', 'u', 'mark', 'span', 'div']);

/** `<font size="n">` de execCommand -> tamaño relativo, para que la nota se vea
 *  proporcionada tanto en la ficha como en un informe impreso. */
const FONT_SIZE_EM = { 1: '.75em', 2: '.85em', 3: '1em', 4: '1.15em', 5: '1.35em', 6: '1.7em', 7: '2em' };

const RE_COLOR = /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\)|[a-z]{3,20})$/i;
const RE_FONT_FAMILY = /^[a-z0-9\s,'"-]{1,80}$/i;
const RE_FONT_SIZE = /^(\d{1,3}(\.\d+)?(px|pt|em|rem|%)|x?x-(small|large)|small|medium|large|smaller|larger)$/i;
const ALIGNS = new Set(['left', 'center', 'right', 'justify']);

/** Fondo que NO es un resaltado (el navegador los emite al quitar uno). */
const EMPTY_BG = ['transparent', 'initial', 'unset', 'none', 'inherit', ''];

function styleOf(node) {
  const raw = (node.getAttribute && node.getAttribute('style')) || '';
  const out = {};
  for (const decl of String(raw).split(';')) {
    const i = decl.indexOf(':');
    if (i < 0) continue;
    out[decl.slice(0, i).trim().toLowerCase()] = decl.slice(i + 1).trim();
  }
  return out;
}

/**
 * Estilos que se conservan, ya validados. Se descarta cualquier valor que no
 * encaje con su patrón: más vale perder un color raro que dejar pasar un
 * `url(...)` o una `expression(...)` dentro del atributo style.
 */
function safeStyle(css, tag) {
  const out = {};
  const color = css.color;
  if (color && RE_COLOR.test(color)) out.color = color;
  const family = css['font-family'];
  if (family && RE_FONT_FAMILY.test(family)) out.fontFamily = family;
  const size = css['font-size'];
  if (size && RE_FONT_SIZE.test(size)) out.fontSize = size;
  // La alineación solo tiene sentido en un bloque; en un <span> el navegador la
  // ignora y guardarla sería ruido.
  const align = css['text-align'];
  if (align && ALIGNS.has(align.toLowerCase()) && tag === 'div') out.textAlign = align.toLowerCase();
  return out;
}

const hasStyle = (s) => !!s && Object.keys(s).length > 0;

/**
 * Nodo del DOM -> árbol normalizado. El árbol es la representación única con la
 * que trabajan el resto de funciones (HTML, marcas y React), así que la lista
 * blanca se aplica en un solo sitio.
 *
 * Normaliza además la forma de decir lo mismo: `font-weight:bold` y `<strong>`
 * terminan los dos en `b`, `font-style:italic` en `i`, un fondo de color en
 * `mark`. Sin esto, la nota dependería de qué etiqueta le apeteció emitir al
 * navegador y la vuelta a marcas no sería fiable.
 *
 * Nodos: {t:'text',v} | {t:'br'} | {t:'el',tag,style,kids}
 */
function walkNode(node, out) {
  for (const child of (node.childNodes || [])) {
    if (child.nodeType === 3) {
      const v = child.nodeValue || '';
      if (v) out.push({ t: 'text', v });
      continue;
    }
    if (child.nodeType !== 1) continue; // comentarios y demás
    const tag = String(child.tagName || '').toLowerCase();
    if (tag === 'br') { out.push({ t: 'br' }); continue; }
    // Nunca se desciende a estas: su texto interno no es texto de la nota.
    if (tag === 'script' || tag === 'style' || tag === 'noscript' || tag === 'template') continue;

    const css = styleOf(child);
    const wraps = [];
    if (tag === 'b' || tag === 'strong' || /^(bold|[6-9]00)$/i.test(css['font-weight'] || '')) wraps.push('b');
    if (tag === 'i' || tag === 'em' || /italic|oblique/i.test(css['font-style'] || '')) wraps.push('i');
    if (tag === 'u' || /underline/i.test(css['text-decoration'] || '') || /underline/i.test(css['text-decoration-line'] || '')) wraps.push('u');
    const bg = (css['background-color'] || css.background || '').trim().toLowerCase();
    if (tag === 'mark' || (!EMPTY_BG.includes(bg) && RE_COLOR.test(bg))) wraps.push('mark');

    // <font size|face|color> de execCommand: se traduce a estilos.
    let extra = {};
    if (tag === 'font') {
      const size = child.getAttribute && child.getAttribute('size');
      const face = child.getAttribute && child.getAttribute('face');
      const color = child.getAttribute && child.getAttribute('color');
      if (size && FONT_SIZE_EM[size]) extra.fontSize = FONT_SIZE_EM[size];
      if (face && RE_FONT_FAMILY.test(face)) extra.fontFamily = face;
      if (color && RE_COLOR.test(color)) extra.color = color;
    }

    const isBlock = tag === 'div' || tag === 'p' || tag === 'li';
    const container = isBlock ? 'div' : 'span';
    const style = { ...safeStyle(css, container), ...extra };

    const kids = [];
    walkNode(child, kids);

    // Se construye de dentro hacia fuera: primero el contenedor con estilo (si
    // aporta algo o es un bloque de verdad), y encima los envoltorios b/i/u/mark.
    let inner = kids;
    if (container !== 'span' || hasStyle(style)) {
      inner = [{ t: 'el', tag: container, style, kids }];
    }
    for (const w of wraps) inner = [{ t: 'el', tag: w, style: {}, kids: inner }];
    out.push(...inner);
  }
}

/** Quita nodos vacíos para que el HTML guardado no crezca con cada edición. */
function prune(nodes) {
  const out = [];
  for (const n of nodes) {
    if (n.t === 'text') { if (n.v) out.push(n); continue; }
    if (n.t === 'br') { out.push(n); continue; }
    const kids = prune(n.kids || []);
    const keep = kids.length > 0 || n.tag === 'div';
    if (keep) out.push({ ...n, kids });
  }
  return out;
}

/**
 * Árbol normalizado a partir de un nodo del DOM (al guardar) o de una cadena
 * guardada (al leer). Es la única puerta de entrada: todo lo que se pinta o se
 * vuelve a guardar ha pasado por aquí.
 */
export function parseNoteTree(source) {
  if (source && typeof source === 'object' && source.childNodes) return prune(collect(source));
  const src = String(source ?? '');
  if (!isRichNote(src)) return null; // texto con marcas: no es un árbol
  const html = src.slice(RICH_NOTE_PREFIX.length);
  if (typeof DOMParser === 'undefined') return [{ t: 'text', v: stripTags(html) }];
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  return prune(collect(doc.body));
}

function collect(node) { const out = []; walkNode(node, out); return out; }

/** Último recurso sin DOM: el texto sin etiquetas. */
function stripTags(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(div|p|li)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

/** ¿El árbol lleva algo que las marcas de texto no saben guardar? */
function needsHtml(nodes) {
  for (const n of nodes) {
    if (n.t !== 'el') continue;
    if (n.tag === 'i' || n.tag === 'u') return true;
    if (hasStyle(n.style)) return true;
    if (needsHtml(n.kids || [])) return true;
  }
  return false;
}

const styleToCss = (s) => Object.entries(s || {})
  .map(([k, v]) => `${k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}:${v}`)
  .join(';');

/** Árbol -> HTML acotado (sin el centinela). */
function treeToHtml(nodes) {
  let out = '';
  for (const n of nodes) {
    if (n.t === 'text') { out += escapeHtml(n.v); continue; }
    if (n.t === 'br') { out += '<br>'; continue; }
    const tag = TAGS.has(n.tag) ? n.tag : 'span';
    // El resaltado tiene un solo color en toda la app (el que pinta el boton),
    // asi que se emite siempre el mismo y no se arrastra el que trajera un pegado.
    const css = styleToCss(n.style);
    const style = tag === 'mark' ? ` style="${MARK_STYLE}"` : (css ? ` style="${escapeAttr(css)}"` : '');
    out += `<${tag}${style}>${treeToHtml(n.kids || [])}</${tag}>`;
  }
  return out;
}

/** Árbol -> marcas de texto plano (solo válido si !needsHtml). */
function treeToMarkers(nodes, bold, mark) {
  let out = '';
  for (const n of nodes) {
    if (n.t === 'text') { out += bold ? `**${n.v}**` : mark ? `==${n.v}==` : n.v; continue; }
    if (n.t === 'br') { out += '\n'; continue; }
    if (out && n.tag === 'div') out += '\n';
    out += treeToMarkers(n.kids || [], bold || n.tag === 'b', mark || n.tag === 'mark');
  }
  return out;
}

/** Stored markers -> the HTML the contenteditable shows. */
export function noteMarkersToHtml(text) {
  const src = String(text ?? '');
  if (!src) return '';
  if (isRichNote(src)) {
    // Se vuelve a limpiar al leer: lo guardado pudo entrar por la API sin pasar
    // por este editor.
    const tree = parseNoteTree(src);
    return treeToHtml(tree || []);
  }
  let out = '';
  const re = /(\*\*([\s\S]+?)\*\*|==([\s\S]+?)==)/g;
  let last = 0;
  let m;
  while ((m = re.exec(src)) !== null) {
    out += escapeHtml(src.slice(last, m.index));
    if (m[2] != null) out += `<b>${escapeHtml(m[2])}</b>`;
    else out += `<mark style="${MARK_STYLE}">${escapeHtml(m[3])}</mark>`;
    last = re.lastIndex;
  }
  out += escapeHtml(src.slice(last));
  return out.replace(/\n/g, '<br>');
}

/**
 * DOM -> cadena guardada.
 *
 * Devuelve TEXTO CON MARCAS mientras la nota solo lleve negrita y resaltado —el
 * comportamiento de siempre, sin migrar nada— y solo asciende a HTML acotado
 * cuando aparece algo que las marcas no pueden representar. Así la inmensa
 * mayoría de las notas siguen siendo texto plano en la base de datos.
 *
 * Acepta cualquier nodo con childNodes / nodeType / tagName / getAttribute, así
 * que se puede probar sin navegador.
 */
export function noteHtmlToMarkers(root) {
  const tree = prune(collect(root));
  if (needsHtml(tree)) {
    const html = treeToHtml(tree);
    return html ? RICH_NOTE_PREFIX + html : '';
  }
  return treeToMarkers(tree, false, false)
    // Nodos de texto hermanos generan marcas pegadas ("**a****b**"): se juntan,
    // se tiran las parejas vacías y no se acumulan líneas en blanco.
    .replace(/\*\*\*\*/g, '')
    .replace(/====/g, '')
    .replace(/\*\*\s*\*\*/g, ' ')
    .replace(/==\s*==/g, ' ')
    .replace(/\n{3,}/g, '\n\n');
}

/**
 * Cualquier nota -> texto plano. Para lo que no sabe pintar formato: CSV,
 * cuerpos de correo, vistas previas, contexto de la IA.
 */
export function noteToPlainText(text) {
  const src = String(text ?? '');
  if (!src) return '';
  if (!isRichNote(src)) {
    return src.replace(/\*\*([\s\S]+?)\*\*/g, '$1').replace(/==([\s\S]+?)==/g, '$1');
  }
  const tree = parseNoteTree(src);
  if (!tree) return stripTags(src.slice(RICH_NOTE_PREFIX.length));
  let out = '';
  const walk = (nodes) => {
    for (const n of nodes) {
      if (n.t === 'text') { out += n.v; continue; }
      if (n.t === 'br') { out += '\n'; continue; }
      if (out && !out.endsWith('\n') && n.tag === 'div') out += '\n';
      walk(n.kids || []);
    }
  };
  walk(tree);
  return out.replace(/\n{3,}/g, '\n\n');
}
